import { useMemo } from "react";
import { useForm } from "@tanstack/react-form";
import { inferRouterOutputs } from "@trpc/server/unstable-core-do-not-import";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import type { AppRouter } from "cadendar-trpc-server";
import Button from "../../main/components/Button.tsx";
import { trpc } from "../../main/components/trpc.ts";
import useUser from "../hooks/useUser.ts";
import { AssistantScheduleEditor } from "../../assistantSchedules/components/AssistantScheduleEditor.tsx";

type SalaryType = "gross" | "net";

interface SecretaryContractDraft {
  fullname: string;
  username: string;
  active: boolean;
  hireDate: string;
  salaryAmount: string;
  salaryType: SalaryType | "";
  averageWeeklyHours: string;
  ticketRestaurantAmount: string;
  ticketRestaurantEmployerPart: string;
  socialSecurityNumber: string;
  payrollFirstName: string;
  payrollLastName: string;
}

type SecretaryContractRow =
  inferRouterOutputs<AppRouter>["user"]["getSecretaryContractsForDoctor"][number];

function normalizeDate(value: string | Date | null | undefined) {
  if (!value) {
    return "";
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISODate() || "";
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toISODate() || "" : "";
}

function normalizeSalaryInput(value: string) {
  return value.replace(/[^0-9,.]/g, "").replace(/,/g, ".");
}

function normalizeSocialSecurityNumber(value: string) {
  return value.replace(/\D/g, "").slice(0, 15);
}

function isValidSalaryAmount(value: string) {
  return value === "" || /^\d+(\.\d{1,2})?$/.test(value);
}

function isValidAverageWeeklyHours(value: string) {
  if (value === "") {
    return true;
  }

  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
    return false;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function formatDecimalAmount(value: number) {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function isValidSocialSecurityNumber(value: string) {
  return value === "" || /^\d{15}$/.test(value);
}

function isValidPercent(value: string) {
  if (value === "") {
    return true;
  }

  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
    return false;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
}

function isValidHireDate(value: string) {
  return value === "" || DateTime.fromISO(value).isValid;
}

function buildDraft(secretary: SecretaryContractRow) {
  return {
    fullname: secretary.fullname,
    username: secretary.username,
    active: secretary.active,
    hireDate: normalizeDate(secretary.hire_date),
    salaryAmount: secretary.salary_amount || "",
    salaryType:
      secretary.salary_type === "gross" || secretary.salary_type === "net"
        ? secretary.salary_type
        : "",
    averageWeeklyHours: secretary.average_weekly_hours || "",
    ticketRestaurantAmount: secretary.ticket_restaurant_amount || "",
    ticketRestaurantEmployerPart:
      secretary.ticket_restaurant_employer_part || "",
    socialSecurityNumber: secretary.social_security_number || "",
    payrollFirstName: secretary.payroll_first_name || "",
    payrollLastName: secretary.payroll_last_name || "",
  } satisfies SecretaryContractDraft;
}

function getSecretaryContractVersion(secretary: SecretaryContractRow) {
  return JSON.stringify(buildDraft(secretary));
}

function SecretaryContractCard({ secretary }: { secretary: SecretaryContractRow }) {
  const trpcUtils = trpc.useUtils();
  const defaultValues = buildDraft(secretary);

  const updateMutation = trpc.user.updateSecretaryContractByDoctor.useMutation({
    onSuccess: async () => {
      await trpcUtils.user.getSecretaryContractsForDoctor.invalidate();
      toast.success("Contrat enregistré.");
    },
    onError: () => {
      toast.error("Impossible d'enregistrer le contrat.");
    },
  });

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      updateMutation.mutate({
        secretaryId: secretary.id,
        hireDate: value.hireDate || null,
        salaryAmount: value.salaryAmount || null,
        salaryType:
          value.salaryType === "gross" || value.salaryType === "net"
            ? value.salaryType
            : null,
        averageWeeklyHours: value.averageWeeklyHours || null,
        ticketRestaurantAmount: value.ticketRestaurantAmount || null,
        ticketRestaurantEmployerPart: value.ticketRestaurantEmployerPart || null,
        socialSecurityNumber: value.socialSecurityNumber || null,
        payrollFirstName: value.payrollFirstName || null,
        payrollLastName: value.payrollLastName || null,
      });
    },
  });

  return (
    <form.Subscribe
      selector={(state) => ({
        draft: state.values,
        isDirty: state.isDirty,
        isSubmitting: state.isSubmitting,
      })}
    >
      {({ draft, isDirty, isSubmitting }) => {
        const canSave =
          isDirty &&
          isValidHireDate(draft.hireDate) &&
          isValidSalaryAmount(draft.salaryAmount) &&
          isValidAverageWeeklyHours(draft.averageWeeklyHours) &&
          isValidSalaryAmount(draft.ticketRestaurantAmount) &&
           isValidPercent(draft.ticketRestaurantEmployerPart) &&
           isValidSocialSecurityNumber(draft.socialSecurityNumber) &&
           draft.payrollFirstName.length <= 100 &&
           draft.payrollLastName.length <= 100 &&
           (draft.salaryAmount === "" || draft.salaryType !== "");
        const formattedHireDate = draft.hireDate
          ? DateTime.fromISO(draft.hireDate).toFormat("dd/MM/yyyy")
          : null;
        const salaryAmount = Number(draft.salaryAmount);
        const averageWeeklyHours = Number(draft.averageWeeklyHours);
        const averageMonthlyHours =
          Number.isFinite(averageWeeklyHours) && averageWeeklyHours > 0
            ? (averageWeeklyHours * 52) / 12
            : null;
        const hourlySalary =
          Number.isFinite(salaryAmount) && salaryAmount > 0 && averageMonthlyHours
            ? salaryAmount / averageMonthlyHours
            : null;
        const hourlySalaryLabel =
          draft.salaryType === "gross"
            ? "Salaire horaire brut"
            : draft.salaryType === "net"
              ? "Salaire horaire net"
              : "Salaire horaire";

        return (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-start justify-between gap-4 max-md:flex-col">
              <div>
                <div className="text-xl font-semibold text-slate-900">
                  {secretary.fullname}
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  @{secretary.username}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  title={isSubmitting ? "Enregistrement..." : "Enregistrer"}
                  style="main"
                  onClick={form.handleSubmit}
                  disabled={!canSave || isSubmitting || updateMutation.isPending}
                />
                <Button
                  title="Annuler"
                  style="secondary"
                  onClick={() => {
                    form.reset();
                  }}
                  disabled={!isDirty || isSubmitting || updateMutation.isPending}
                />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4 max-md:grid-cols-1">
              <form.Field
                name="payrollLastName"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Nom pour la fiche de paie
                    </div>
                    <input
                      type="text"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      maxLength={100}
                      onChange={(event) => {
                        field.handleChange(event.target.value);
                      }}
                      placeholder="Dupont"
                    />
                  </label>
                )}
              />

              <form.Field
                name="payrollFirstName"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Prénom pour la fiche de paie
                    </div>
                    <input
                      type="text"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      maxLength={100}
                      onChange={(event) => {
                        field.handleChange(event.target.value);
                      }}
                      placeholder="Jeanne"
                    />
                  </label>
                )}
              />

              <form.Field
                name="hireDate"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Date d&apos;embauche
                    </div>
                    <input
                      type="date"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(event.target.value);
                      }}
                    />
                    {formattedHireDate ? (
                      <div className="mt-2 text-xs text-slate-500">
                        Affichage: {formattedHireDate}
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="socialSecurityNumber"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Numéro de sécurité sociale
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(
                          normalizeSocialSecurityNumber(event.target.value)
                        );
                      }}
                      placeholder="15 chiffres"
                    />
                    {!isValidSocialSecurityNumber(field.state.value) ? (
                      <div className="mt-2 text-xs text-red-600">
                        Le numéro de sécurité sociale doit contenir 15 chiffres.
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="salaryAmount"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Salaire mensuel
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(normalizeSalaryInput(event.target.value));
                      }}
                      placeholder="2500"
                    />
                    {!isValidSalaryAmount(field.state.value) ? (
                      <div className="mt-2 text-xs text-red-600">
                        Entrez un montant valide, avec au maximum deux décimales.
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="salaryType"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Salaire défini en
                    </div>
                    <select
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        const value = event.target.value;
                        field.handleChange(
                          value === "gross" || value === "net" ? value : ""
                        );
                      }}
                    >
                      <option value="">Choisir</option>
                      <option value="gross">Brut</option>
                      <option value="net">Net</option>
                    </select>
                    {draft.salaryAmount !== "" && field.state.value === "" ? (
                      <div className="mt-2 text-xs text-red-600">
                        Choisissez si le salaire est net ou brut.
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="averageWeeklyHours"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Heures moyennes par semaine
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(normalizeSalaryInput(event.target.value));
                      }}
                      placeholder="35"
                    />
                    {!isValidAverageWeeklyHours(field.state.value) ? (
                      <div className="mt-2 text-xs text-red-600">
                        Entrez un nombre d&apos;heures valide, supérieur à 0.
                      </div>
                    ) : null}
                    {averageMonthlyHours ? (
                      <div className="mt-2 text-xs text-slate-500">
                        Temps de travail mensuel moyen: {formatDecimalAmount(averageMonthlyHours)} h/mois
                      </div>
                    ) : null}
                    {hourlySalary ? (
                      <div className="mt-2 text-sm font-medium text-slate-700">
                        {hourlySalaryLabel}: {formatDecimalAmount(hourlySalary)} EUR
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="ticketRestaurantAmount"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Valeur du ticket restaurant
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(normalizeSalaryInput(event.target.value));
                      }}
                      placeholder="10"
                    />
                    {!isValidSalaryAmount(field.state.value) ? (
                      <div className="mt-2 text-xs text-red-600">
                        Entrez un montant valide, avec au maximum deux décimales.
                      </div>
                    ) : null}
                  </label>
                )}
              />

              <form.Field
                name="ticketRestaurantEmployerPart"
                children={(field) => (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Part employeur tickets restaurant
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                      value={field.state.value}
                      onChange={(event) => {
                        field.handleChange(normalizeSalaryInput(event.target.value));
                      }}
                      placeholder="60"
                    />
                    {!isValidPercent(field.state.value) ? (
                      <div className="mt-2 text-xs text-red-600">
                        Entrez un pourcentage valide entre 0 et 100.
                      </div>
                    ) : null}
                  </label>
                )}
              />
            </div>

            <div className="mt-6 border-t border-slate-200 pt-6">
              <div className="mb-3">
                <div className="text-lg font-semibold text-slate-900">
                  Planning de l&apos;assistante
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  Définissez les horaires de travail propres à cette secrétaire/assistante.
                </div>
              </div>
              <AssistantScheduleEditor
                secretaryId={secretary.id}
                contractStartDate={draft.hireDate || null}
                assistantName={secretary.fullname}
              />
            </div>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

export default function SecretaryContractsPage() {
  const user = useUser();
  const { data: contracts, isLoading } =
    trpc.user.getSecretaryContractsForDoctor.useQuery(undefined, {
      enabled: user?.tag === "doctor",
    });

  const typedContracts = useMemo(() => {
    return (contracts || []).filter((secretary) => secretary.active);
  }, [contracts]);

  if (!user || user.tag !== "doctor") {
    return (
      <div className="min-h-full bg-slate-100 p-7">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-7 shadow-sm">
          Accès réservé aux docteurs.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-4">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-[28px] bg-white p-7 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
            Contrats des secrétaires/Assistantes
          </h1>
          <p className="mb-0 mt-3 text-base text-slate-600">
            Définissez la date d&apos;embauche, le salaire, les heures hebdomadaires
            moyennes, les tickets restaurant et le numéro de sécurité sociale de
            chaque secrétaire.
          </p>

          {isLoading ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-600">
              Chargement...
            </div>
          ) : null}

          {!isLoading && typedContracts.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-600">
              Aucune secrétaire liée à votre compte.
            </div>
          ) : null}

          <div className="mt-6 space-y-5">
            {typedContracts.map((secretary) => (
              <SecretaryContractCard
                key={`${secretary.id}:${getSecretaryContractVersion(secretary)}`}
                secretary={secretary}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
